

import React, { useState, useEffect, useCallback } from 'react';
import { Step1TaxablePersonDetails } from './components/forms/Step1_TaxablePersonDetails';
import { Step2Elections } from './components/forms/Step2_Elections';
import { Step3Accounting } from './components/forms/Step3_Accounting';
import { Step4AccountingAdjustments } from './components/forms/Step4_AccountingAdjustments';
import { Step5Relief } from './components/forms/Step5_Relief';
import { Step6OtherAdjustments } from './components/forms/Step6_OtherAdjustments';
import { Step7TaxLiability } from './components/forms/Step7_TaxLiability';
import { ReviewAndDeclaration } from './components/forms/Step8_Review';
import { StepNavigation } from './components/StepNavigation';
import { FormData, initialFormData, Step, FormErrors, FormErrorsStep, ScheduleName, CurrentScheduleInfo, ScheduleDataTypes, TranslationFunction, Schedule17TaxLossData, Schedule15TaxCreditData, Schedule19InterestCappingData, GenericScheduleProps, Schedule12IPData, Schedule13UaeDividendsData, Schedule14ForeignPEData, Schedule16RelatedPartyData, Schedule16ConnectedPersonsData, Schedule17TaxGroupLossData, Schedule18ParticipationExemptionData, Schedule20ReliefData, Schedule21TransitionalRulesData, Schedule22OtherSubScheduleData, Schedule22_4_AttachmentsData, FreeZoneScheduleData } from './types';
import { saveForm, loadForm, clearForm } from './utils/localStorage';
import { 
  validateStep1, validateStep2, validateStep3, validateStep4,
  validateStep5, validateStep6, validateStep7,
  validateSchedule17TaxLosses, validateSchedule15TaxCredits, validateSchedule19InterestCapping,
  validateFreeZoneSchedule,
  validateSchedule12IP, validateSchedule13UaeDividends, validateSchedule14ForeignPE,
  validateSchedule16RelatedParty, validateSchedule16ConnectedPersons, validateSchedule17TaxGroupLosses,
  validateSchedule18ParticipationExemption, validateSchedule20Relief, validateSchedule21TransitionalRules,
  validateSchedule22Other, validateSchedule22_4_Attachments
} from './utils/validation';
import { printAsPdf, exportToExcel } from './utils/pdfExport';
import { Language, translations } from './i18n/translations';
import { ScheduleModalWrapper } from './components/schedules/ScheduleModalWrapper';

import { Schedule17TaxLosses } from './components/schedules/Schedule17_TaxLosses';
import { Schedule15TaxCredits } from './components/schedules/Schedule15_TaxCredits';
import { Schedule19_InterestCapping } from './components/schedules/Schedule19_InterestCapping';
import { Schedule12_2_FreeZoneIP } from './components/schedules/Schedule12_2_FreeZoneIP';
import { Schedule13_UaeDividends } from './components/schedules/Schedule13_UaeDividends';
import { Schedule14_ForeignPE } from './components/schedules/Schedule14_ForeignPE';
import { Schedule16_1_RelatedParty } from './components/schedules/Schedule16_1_RelatedParty';
import { Schedule16_2_ConnectedPersons } from './components/schedules/Schedule16_2_ConnectedPersons';
import { Schedule17_TaxGroupLosses } from './components/schedules/Schedule17_TaxGroupLosses';
import { Schedule18_ParticipationExemption } from './components/schedules/Schedule18_ParticipationExemption';
import { Schedule20_TaxRelief } from './components/schedules/Schedule20_TaxRelief';
import { Schedule21_TransitionalRules } from './components/schedules/Schedule21_TransitionalRules';
import { Schedule22_Other } from './components/schedules/Schedule22_Other';
import { Schedule22_4_Attachments } from './components/schedules/Schedule22_4_Attachments';
import { FreeZoneSchedule } from './components/schedules/FreeZoneSchedule';
import { ExemptPersonInfo } from './components/forms/ExemptPersonInfo';
import { safeParseFloat } from './utils/calculations';


const SBR_REVIEW_STEP_INDEX = 2; 
const EXEMPT_PERSON_NO_TAXABLE_BUSINESS_REVIEW_INDEX = 1;


const App: React.FC = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>(loadForm() || initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSBRPath, setIsSBRPath] = useState<boolean>(formData.elections.smallBusinessRelief || false);
  const [isExemptNoTaxableBusinessPath, setIsExemptNoTaxableBusinessPath] = useState<boolean>(
    (formData.taxablePersonDetails.isExemptPerson === 'yes' && formData.taxablePersonDetails.exemptPersonHasTaxableBusiness === 'no') || false
  );
  const [language, setLanguage] = useState<Language>('en');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [currentSchedule, setCurrentSchedule] = useState<CurrentScheduleInfo>(null);

  const t = useCallback((key: keyof typeof translations.en, options?: Record<string, string | number>) => {
    const translation = translations[language][key] || translations.en[key] || key;
    if (options && typeof translation === 'string') {
      return Object.entries(options).reduce((acc, [optKey, optValue]) => {
        return acc.replace(`{${optKey}}`, String(optValue));
      }, translation);
    }
    return translation;
  }, [language]);
  
  useEffect(() => {
    if (!isLoaded) { 
        const loadedData = loadForm();
        if (loadedData) {
            const updatedAttachments = initialFormData.schedule22_4_Attachments.attachments.map(initAtt => {
                const loadedAtt = loadedData.schedule22_4_Attachments?.attachments.find(la => la.documentKey === initAtt.documentKey);
                return { 
                  ...initAtt, 
                  ...(loadedAtt || {}), 
                  documentName: t(initAtt.documentKey as any) 
                };
            });
            
            initialFormData.schedule22_4_Attachments.attachments.forEach(initAtt => {
              if (!updatedAttachments.find(att => att.documentKey === initAtt.documentKey)) {
                updatedAttachments.push({ ...initAtt, documentName: t(initAtt.documentKey as any) });
              }
            });

            loadedData.schedule22_4_Attachments = { attachments: updatedAttachments };

            setFormData(loadedData);
            setIsSBRPath(loadedData.elections.smallBusinessRelief || false);
            setIsExemptNoTaxableBusinessPath(
              (loadedData.taxablePersonDetails.isExemptPerson === 'yes' && loadedData.taxablePersonDetails.exemptPersonHasTaxableBusiness === 'no') || false
            );
        } else {
            const initialAttachmentsWithNames = initialFormData.schedule22_4_Attachments.attachments.map(att => ({
                ...att,
                documentName: t(att.documentKey as any)
            }));
            setFormData(prev => ({ ...prev, schedule22_4_Attachments: { attachments: initialAttachmentsWithNames } }));
        }
        setIsLoaded(true);
    }
  }, [isLoaded, t]); 


  useEffect(() => {
    if(isLoaded) {
      saveForm(formData);
      if (formData.elections.smallBusinessRelief !== isSBRPath) {
        setIsSBRPath(formData.elections.smallBusinessRelief);
      }
      const currentExemptPath = (formData.taxablePersonDetails.isExemptPerson === 'yes' && formData.taxablePersonDetails.exemptPersonHasTaxableBusiness === 'no');
      if (currentExemptPath !== isExemptNoTaxableBusinessPath) {
        setIsExemptNoTaxableBusinessPath(currentExemptPath);
      }
    }
  }, [formData, isLoaded, isSBRPath, isExemptNoTaxableBusinessPath]);

  const baseStepsBeforeSBRBranch: Step[] = React.useMemo(() => [
    { name: t('step1_title'), component: Step1TaxablePersonDetails, validation: validateStep1, errorKey: 'taxablePersonDetails' },
  ], [t]);

  const electionsStep: Step = React.useMemo(() => (
    { name: t('step2_title'), component: Step2Elections, validation: validateStep2, errorKey: 'elections' }
  ), [t]);

  const normalPathStepsAfterSBRBranch: Step[] = React.useMemo(() => [
    { name: t('step3_title'), component: Step3Accounting, validation: validateStep3, errorKey: 'accounting' },
    { name: t('step4_title'), component: Step4AccountingAdjustments, validation: validateStep4, errorKey: 'accountingAdjustments' },
    { name: t('step5_title'), component: Step5Relief, validation: validateStep5, errorKey: 'relief' },
    { name: t('step6_title'), component: Step6OtherAdjustments, validation: validateStep6, errorKey: 'otherAdjustments' },
    { name: t('step7_title'), component: Step7TaxLiability, validation: validateStep7, errorKey: 'taxLiability' },
  ], [t]);
  
  const reviewStepConfig: Step = React.useMemo(() => (
    { name: t('step8_title'), component: ReviewAndDeclaration, validation: () => ({}), errorKey: 'review' }
  ), [t]);
  
  const exemptPersonInfoStep: Step = React.useMemo(() => (
    { name: t('exempt_person_info_step_title'), component: ExemptPersonInfo, validation: () => ({}), errorKey: 'review' } 
  ), [t]);


  const stepsConfig: Step[] = React.useMemo(() => {
    if (isExemptNoTaxableBusinessPath) {
      return [baseStepsBeforeSBRBranch[0], exemptPersonInfoStep]; 
    }
    if (isSBRPath) {
      return [baseStepsBeforeSBRBranch[0], electionsStep, reviewStepConfig]; 
    }
    return [baseStepsBeforeSBRBranch[0], electionsStep, ...normalPathStepsAfterSBRBranch, reviewStepConfig];
  }, [isSBRPath, isExemptNoTaxableBusinessPath, baseStepsBeforeSBRBranch, electionsStep, normalPathStepsAfterSBRBranch, reviewStepConfig, exemptPersonInfoStep]); 
  
  const CurrentStepComponent = stepsConfig[currentStepIndex].component;

  const validateCurrentStep = (): boolean => {
    const currentStepConfig = stepsConfig[currentStepIndex];
    if (currentStepConfig.component === ExemptPersonInfo) {
        return true;
    }
    const currentValidator = currentStepConfig.validation;
    const errorKey = currentStepConfig.errorKey;

    if (currentValidator && errorKey) {
      const stepValidationErrors: FormErrorsStep = currentValidator(formData, t);
      setErrors(prevErrors => ({
        ...prevErrors,
        [errorKey]: stepValidationErrors,
      }));
      return Object.keys(stepValidationErrors).length === 0;
    }
    if (errorKey) {
        setErrors(prevErrors => {
            if (prevErrors[errorKey] && Object.keys(prevErrors[errorKey]!).length > 0) {
                 const newErrors = {...prevErrors};
                 delete newErrors[errorKey];
                 return newErrors;
            }
            return prevErrors;
        });
    }
    return true;
  };

 const handleNext = () => {
    if (!validateCurrentStep()) {
      return;
    }
    if (currentStepIndex < stepsConfig.length - 1) {
      let nextStepIndex = currentStepIndex + 1;
      if (currentStepIndex === 1 && isSBRPath && !isExemptNoTaxableBusinessPath) { 
        nextStepIndex = stepsConfig.findIndex(step => step.component === ReviewAndDeclaration);
      }
      setCurrentStepIndex(nextStepIndex);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      let prevStepIndex = currentStepIndex - 1;
      if (currentStepIndex === stepsConfig.findIndex(step => step.component === ReviewAndDeclaration) && isSBRPath && !isExemptNoTaxableBusinessPath) {
        prevStepIndex = 1; 
      }
      setCurrentStepIndex(prevStepIndex);
    }
  };

  const handleDataChange = (sectionOrScheduleName: keyof FormData | ScheduleName, field: string, value: any) => {
    setFormData(prev => {
      const newFormData = { ...prev };
      if (sectionOrScheduleName in prev) { 
        newFormData[sectionOrScheduleName as keyof FormData] = {
            ...(prev[sectionOrScheduleName as keyof FormData] as any),
            [field]: value,
          };
      } else { 
        newFormData[sectionOrScheduleName as ScheduleName] = { 
            ...(prev[sectionOrScheduleName as ScheduleName] as any),
            [field]: value,
          }
      }

      if (sectionOrScheduleName === 'taxablePersonDetails' && (field === 'isExemptPerson' || field === 'exemptPersonHasTaxableBusiness')) {
        const newExemptPath = (newFormData.taxablePersonDetails.isExemptPerson === 'yes' && newFormData.taxablePersonDetails.exemptPersonHasTaxableBusiness === 'no');
        if (newExemptPath !== isExemptNoTaxableBusinessPath) {
            if ((newExemptPath && currentStepIndex > EXEMPT_PERSON_NO_TAXABLE_BUSINESS_REVIEW_INDEX) || (!newExemptPath && isExemptNoTaxableBusinessPath)) {
                setCurrentStepIndex(0);
            }
        }
      }
      if (sectionOrScheduleName === 'elections' && field === 'smallBusinessRelief') {
         const newSBRPath = !!value; 
         if (newSBRPath !== isSBRPath) {
            if ((newSBRPath && currentStepIndex > SBR_REVIEW_STEP_INDEX ) || (!newSBRPath && isSBRPath)) {
                 setCurrentStepIndex(0);
            }
         }
      }
      return newFormData;
    });

    const errorKey = stepsConfig[currentStepIndex]?.errorKey; 
    if (errorKey && errors[errorKey] && errors[errorKey]![field]) {
        setErrors(prev => {
            const newStepErrors = {...prev[errorKey]};
            delete newStepErrors[field];
            return { ...prev, [errorKey]: newStepErrors };
        });
    }
  };

  const handleScheduleDataChange = (scheduleName: ScheduleName, field: string, value: any) => {
    setCurrentSchedule(prev => {
        if (!prev) return null;
        return {
            ...prev,
            data: {
                ...prev.data,
                [field]: value
            } as ScheduleDataTypes
        };
    });
  };
  
  const openScheduleModal = (scheduleName: ScheduleName, scheduleData: ScheduleDataTypes) => {
    setCurrentSchedule({ name: scheduleName, data: JSON.parse(JSON.stringify(scheduleData)) }); 
  };

  const closeScheduleModal = (savedData?: ScheduleDataTypes) => {
    if (savedData && currentSchedule) {
      let updatedFormData = {
        ...formData,
        [currentSchedule.name]: savedData
      };

      // Auto-populate main form from schedule totals
      if (currentSchedule.name === 'freeZoneSchedule') {
        const fzData = savedData as FreeZoneScheduleData;
        updatedFormData.freeZoneSchedule.revenueIpNonQualifying = fzData.revenueIpNonQualifying || '0.00';
        updatedFormData.freeZoneSchedule.revenueIpQualifying = fzData.revenueIpQualifying || '0.00';
        updatedFormData.freeZoneSchedule.calculatedQualifyingIncome = fzData.calculatedQualifyingIncome || '0.00';
        updatedFormData.freeZoneSchedule.calculatedNonQualifyingIncome = fzData.calculatedNonQualifyingIncome || '0.00';
      } else if (currentSchedule.name === 'schedule12IP') {
        const sch12IPData = savedData as Schedule12IPData;
        updatedFormData.freeZoneSchedule.revenueIpNonQualifying = sch12IPData.totalRevenueFromIPNotQualifyingIncome || '0.00';
        updatedFormData.freeZoneSchedule.revenueIpQualifying = sch12IPData.totalRevenueFromQualifyingIP || '0.00';
      } else if (currentSchedule.name === 'schedule13UaeDividends') {
        const sch13Data = savedData as Schedule13UaeDividendsData;
        updatedFormData.accountingAdjustments.totalDividendsFromUaeResident = sch13Data.totalDividendsReceived || '0.00';
      } else if (currentSchedule.name === 'schedule14ForeignPE') {
        const sch14Data = savedData as Schedule14ForeignPEData;
        updatedFormData.accountingAdjustments.taxableIncomeLossFromEligibleForeignPE = sch14Data.netIncomeLossForAdjustment || '0.00';
      } else if (currentSchedule.name === 'schedule15TaxCredits') {
        const sch15Data = savedData as Schedule15TaxCreditData;
        updatedFormData.taxLiability.taxCreditsApplied = sch15Data.totalTaxCreditsClaimed || '0.00';
      } else if (currentSchedule.name === 'schedule16RelatedParty') {
        const sch16RPData = savedData as Schedule16RelatedPartyData;
        updatedFormData.otherAdjustments.additionsArmslengthAdjustments = sch16RPData.totalUpwardAdjustments || '0.00';
        updatedFormData.otherAdjustments.deductionsArmslengthAdjustments = sch16RPData.totalDownwardAdjustments || '0.00'; 
      } else if (currentSchedule.name === 'schedule16ConnectedPersons') {
        const sch16CPData = savedData as Schedule16ConnectedPersonsData;
        updatedFormData.otherAdjustments.adjustmentsConnectedPersonsNonMarketValue = sch16CPData.totalAdjustments || '0.00';
      } else if (currentSchedule.name === 'schedule17TaxLosses') {
        const sch17Data = savedData as Schedule17TaxLossData;
        updatedFormData.taxLiability.taxLossesUtilisedCurrentPeriod = sch17Data.taxLossesUtilisedInCurrentPeriodFromSchedule || '0.00';
      } else if (currentSchedule.name === 'schedule17TaxGroupLosses') {
        const sch17TGData = savedData as Schedule17TaxGroupLossData;
        updatedFormData.taxLiability.taxLossesUtilisedCurrentPeriod = sch17TGData.taxGroupLossesUtilisedInCurrentPeriod || '0.00';
        updatedFormData.taxLiability.preGroupingTaxLossesUtilisedTaxGroup = sch17TGData.preGroupingLossItems?.reduce((acc, item) => acc + safeParseFloat(item.preGroupingLossUtilised),0).toFixed(2) || '0.00';
        updatedFormData.taxLiability.taxLossesClaimedFromOtherGroupEntitiesTaxGroup = sch17TGData.taxLossesClaimedFromOthers?.reduce((acc, item) => acc + safeParseFloat(item.amount),0).toFixed(2) || '0.00';
      } else if (currentSchedule.name === 'schedule18ParticipationExemption') {
        const sch18Data = savedData as Schedule18ParticipationExemptionData;
        updatedFormData.accountingAdjustments.incomeFromParticipatingInterestExempted = sch18Data.totalIncomeExempted || '0.00';
      } else if (currentSchedule.name === 'schedule19InterestCapping') {
        const sch19Data = savedData as Schedule19InterestCappingData;
        updatedFormData.otherAdjustments.disallowedNetInterestExpCurrentPeriod = sch19Data.finalDisallowedNIEForCurrentPeriod || '0.00';
        updatedFormData.otherAdjustments.broughtForwardNieUtilised = sch19Data.nieUtilisedFromBroughtForward || '0.00';
      } else if (currentSchedule.name === 'schedule20Relief') {
        const sch20Data = savedData as Schedule20ReliefData;
        updatedFormData.relief.totalGainLossNotTaxedQGCurrentPeriod = sch20Data.totalGainLossNotTaxedQGCurrentPeriod || '0.00';
        updatedFormData.relief.totalGainLossRecognisedClawbackQG = sch20Data.totalGainLossRecognisedClawbackQG || '0.00';
        updatedFormData.relief.totalGainLossNotTaxedBRRCurrentPeriod = sch20Data.totalGainLossNotTaxedBRRCurrentPeriod || '0.00';
        updatedFormData.relief.totalGainLossRecognisedClawbackBRR = sch20Data.totalGainLossRecognisedClawbackBRR || '0.00';
        
        updatedFormData.relief.gainsLossesSubjectToTgReliefCurrentPeriod = sch20Data.totalGainLossNotTaxedQGCurrentPeriod || '0.00';
        updatedFormData.relief.gainsLossesFromTgClawback = sch20Data.totalGainLossRecognisedClawbackQG || '0.00';
        updatedFormData.relief.gainsLossesSubjectToBrrReliefCurrentPeriod = sch20Data.totalGainLossNotTaxedBRRCurrentPeriod || '0.00';
        updatedFormData.relief.gainsLossesFromBrrClawback = sch20Data.totalGainLossRecognisedClawbackBRR || '0.00';

      } else if (currentSchedule.name === 'schedule21TransitionalRules') {
        const sch21Data = savedData as Schedule21TransitionalRulesData;
        updatedFormData.accountingAdjustments.gainsOnElectedImmovableProperty = sch21Data.totalAmountExcludedImmovableProperty || '0.00';
        updatedFormData.accountingAdjustments.transitionalRulesIntangibleAssetsAdjustment = sch21Data.totalAmountExcludedIntangibleAssets || '0.00';
        updatedFormData.accountingAdjustments.transitionalRulesFinancialAssetsLiabilitiesAdjustment = 
            (safeParseFloat(sch21Data.totalAmountExcludedFinancialAssets) - 
             safeParseFloat(sch21Data.totalAmountExcludedFinancialLiabilities) 
            ).toFixed(2); 
      } else if (currentSchedule.name === 'schedule22Other') {
        const sch22Data = savedData as Schedule22OtherSubScheduleData;
        updatedFormData.accountingAdjustments.gainsNotInIS = sch22Data.totalIncomeAdjustmentNotInIS || '0.00';
        updatedFormData.accountingAdjustments.lossesNotInIS = sch22Data.totalLossAdjustmentNotInIS || '0.00';
        updatedFormData.accountingAdjustments.unrealisedGainsRecognisedCurrentTaxPeriod = sch22Data.totalUnrealisedGainsExcluded || '0.00';
        updatedFormData.accountingAdjustments.unrealisedLossesRecognisedCurrentTaxPeriod = sch22Data.totalUnrealisedLossesExcluded || '0.00';
        updatedFormData.accountingAdjustments.deferredGainsRealisedCurrentTaxPeriod = sch22Data.totalDeferredGainsRealised || '0.00';
        updatedFormData.accountingAdjustments.deferredLossesRealisedCurrentTaxPeriod = sch22Data.totalDeferredLossesRealised || '0.00';
      }
      
      setFormData(updatedFormData);

      const validator = scheduleValidators[currentSchedule.name];
      if(validator && currentSchedule.name) { 
        let scheduleErrors: FormErrorsStep;
        // Pass fullFormData for validators that need it
        if (['schedule22_4_Attachments', 'schedule17TaxLosses', 'schedule17TaxGroupLosses', 'freeZoneSchedule'].includes(currentSchedule.name)) {
          scheduleErrors = validator(savedData, t, updatedFormData);
        } else {
          scheduleErrors = validator(savedData, t);
        }
        setErrors(prev => ({...prev, [currentSchedule!.name]: scheduleErrors }));
      }
    }
    setCurrentSchedule(null);
  };

  const scheduleValidators: Record<ScheduleName, (data: ScheduleDataTypes, t: TranslationFunction, fullFormData?: FormData) => FormErrorsStep> = {
    schedule17TaxLosses: validateSchedule17TaxLosses,
    schedule15TaxCredits: validateSchedule15TaxCredits,
    schedule19InterestCapping: validateSchedule19InterestCapping,
    freeZoneSchedule: validateFreeZoneSchedule, 
    schedule12IP: validateSchedule12IP,
    schedule13UaeDividends: validateSchedule13UaeDividends,
    schedule14ForeignPE: validateSchedule14ForeignPE,
    schedule16RelatedParty: validateSchedule16RelatedParty,
    schedule16ConnectedPersons: validateSchedule16ConnectedPersons,
    schedule17TaxGroupLosses: validateSchedule17TaxGroupLosses,
    schedule18ParticipationExemption: validateSchedule18ParticipationExemption,
    schedule20Relief: validateSchedule20Relief,
    schedule21TransitionalRules: validateSchedule21TransitionalRules,
    schedule22Other: validateSchedule22Other,
    schedule22_4_Attachments: validateSchedule22_4_Attachments,
  };
  
  const handleResetForm = () => {
    if (window.confirm(t('confirm_reset'))) {
      clearForm();
      setFormData(initialFormData);
      setCurrentStepIndex(0);
      setIsSBRPath(false);
      setIsExemptNoTaxableBusinessPath(false);
      setErrors({});
      setIsLoaded(false); 
    }
  };

  const handlePrint = () => {
    printAsPdf(formData, t);
  };

  const handleExportToExcel = () => {
    exportToExcel(formData, t);
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'ar' : 'en';
    setLanguage(newLang);
    setIsLoaded(false); 
  };

  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  if (!isLoaded) {
    return <div className="flex justify-center items-center h-screen"><p>{t('loading')}</p></div>;
  }

  const currentStepConfig = stepsConfig[currentStepIndex];
  if (!currentStepConfig) { 
    return <div className="flex justify-center items-center h-screen"><p>Error: Invalid step.</p></div>;
  }
  const errorKeyForComponent = currentStepConfig.errorKey;
  const componentErrors: FormErrorsStep = (errorKeyForComponent && errors[errorKeyForComponent]) ? errors[errorKeyForComponent]! : {};

  const renderCurrentSchedule = () => {
    if (!currentSchedule) return null;
    const scheduleSpecificProps: GenericScheduleProps<any> = { 
        scheduleData: currentSchedule.data,
        onChange: (field: string, value: any, index?: number, subField?: string, itemIndex?: number, itemSubField?: string) => {
            if(itemIndex !== undefined && itemSubField !== undefined && subField !== undefined && typeof currentSchedule.data[subField as keyof ScheduleDataTypes] === 'object' && currentSchedule.data[subField as keyof ScheduleDataTypes] !== null && !Array.isArray(currentSchedule.data[subField as keyof ScheduleDataTypes])) {
                // This specific path is for schedule18's nested incomeLossItems array within the participations array.
                if (currentSchedule.name === 'schedule18ParticipationExemption' && subField === 'participations') {
                  const participations = (currentSchedule.data as Schedule18ParticipationExemptionData).participations || [];
                  const updatedParticipations = participations.map((pItem, pIdx) => {
                    if (pIdx === index) { // 'index' here refers to the participation index
                      const incomeLossItems = pItem.incomeLossItems || [];
                      // 'itemIndex' refers to the index within incomeLossItems
                      // 'field' is the field name within the incomeLossItem (e.g., 'natureOfIncomeLoss')
                      // 'value' is the new value for that field
                      // 'itemSubField' would be 'incomeLossItems' if the structure was even deeper, but here it's the field itself
                      const updatedIncomeLossItems = incomeLossItems.map((ilItem, ilIdx) => 
                        ilIdx === itemIndex ? { ...ilItem, [field]: value } : ilItem // Corrected: `field` is the key for the incomeLossItem
                      );
                      return { ...pItem, incomeLossItems: updatedIncomeLossItems };
                    }
                    return pItem;
                  });
                  handleScheduleDataChange(currentSchedule.name, 'participations', updatedParticipations);
                } else {
                  // General handling for a list of objects if needed, though the above is very specific.
                  // This else block might need review if other schedules have similar deeply nested structures.
                  const list = (currentSchedule.data[subField as keyof ScheduleDataTypes] as any[]).slice();
                  list[index!] = { ...list[index!], [field]: value }; 
                  handleScheduleDataChange(currentSchedule.name, subField, list);
                }

            } else if (index !== undefined && subField !== undefined && Array.isArray(currentSchedule.data[subField as keyof ScheduleDataTypes])) {
                const list = (currentSchedule.data[subField as keyof ScheduleDataTypes] as any[]).slice();
                list[index] = { ...list[index], [field]: value };
                handleScheduleDataChange(currentSchedule.name, subField, list);
            } else {
                handleScheduleDataChange(currentSchedule.name, field, value);
            }
        },
        errors: errors[currentSchedule.name] || {},
        t: t,
        fullFormData: formData, 
    };

    switch (currentSchedule.name) {
      case 'schedule17TaxLosses':
        return <Schedule17TaxLosses {...scheduleSpecificProps} />;
      case 'schedule15TaxCredits':
        return <Schedule15TaxCredits {...scheduleSpecificProps} />;
      case 'schedule19InterestCapping':
        return <Schedule19_InterestCapping {...scheduleSpecificProps} />;
      case 'freeZoneSchedule':
        return <FreeZoneSchedule {...scheduleSpecificProps} />;
      case 'schedule12IP':
        return <Schedule12_2_FreeZoneIP {...scheduleSpecificProps} />;
      case 'schedule13UaeDividends':
        return <Schedule13_UaeDividends {...scheduleSpecificProps} />;
      case 'schedule14ForeignPE':
        return <Schedule14_ForeignPE {...scheduleSpecificProps} />;
      case 'schedule16RelatedParty':
        return <Schedule16_1_RelatedParty {...scheduleSpecificProps} />;
      case 'schedule16ConnectedPersons':
        return <Schedule16_2_ConnectedPersons {...scheduleSpecificProps} />;
      case 'schedule17TaxGroupLosses':
         return <Schedule17_TaxGroupLosses {...scheduleSpecificProps} />;
      case 'schedule18ParticipationExemption':
        return <Schedule18_ParticipationExemption {...scheduleSpecificProps} />;
      case 'schedule20Relief':
        return <Schedule20_TaxRelief {...scheduleSpecificProps} />;
      case 'schedule21TransitionalRules':
        return <Schedule21_TransitionalRules {...scheduleSpecificProps} />;
      case 'schedule22Other':
        return <Schedule22_Other {...scheduleSpecificProps} />;
      case 'schedule22_4_Attachments':
        return <Schedule22_4_Attachments {...scheduleSpecificProps} />;
      default:
        console.error("Unknown schedule name:", currentSchedule.name);
        return <p>{t('schedule_not_implemented')}: {currentSchedule.name}</p>;
    }
  };
  const getScheduleTitle = (scheduleName: ScheduleName | undefined): string => {
    if (!scheduleName) return "";
    const key = `${scheduleName}_title` as keyof typeof translations.en;
    if (t(key) !== key && t(key) !== scheduleName) return t(key); 
    
    switch(scheduleName) {
        case 'schedule17TaxLosses': return t('schedule17_title');
        case 'schedule15TaxCredits': return t('schedule15_title');
        case 'schedule19InterestCapping': return t('schedule19_title');
        case 'freeZoneSchedule': return t('fz_schedule_main_title');
        case 'schedule12IP': return t('sch12_2_title');
        case 'schedule13UaeDividends': return t('sch13_title');
        case 'schedule14ForeignPE': return t('sch14_title');
        case 'schedule16RelatedParty': return t('sch16_1_title');
        case 'schedule16ConnectedPersons': return t('sch16_2_title');
        case 'schedule17TaxGroupLosses': return t('sch17_B_title');
        case 'schedule18ParticipationExemption': return t('sch18_title');
        case 'schedule20Relief': return t('sch20_title');
        case 'schedule21TransitionalRules': return t('sch21_title');
        case 'schedule22Other': return t('sch22_title');
        case 'schedule22_4_Attachments': return t('sch22_4_title');
        default: return "Schedule";
    }
};

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <header className="mb-8 max-w-4xl mx-auto bg-[#1a2c46] text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center"> {/* Main flex container for the row */}
          {/* Reset Button - pushed to the left */}
          <button
            onClick={handleResetForm}
            className="no-print bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded shadow transition duration-150 mr-auto"
          >
            {t('reset_form')}
          </button>

          {/* Title - centered */}
          <h1 className="text-3xl font-bold text-[#b98e4c] mx-auto text-center">{t('app_title_practice')}</h1>

          {/* Language Button - pushed to the right */}
          <button
            onClick={toggleLanguage}
            className="no-print bg-gray-200 hover:bg-gray-300 text-[#1a2c46] font-semibold py-2 px-4 rounded shadow transition duration-150 ml-auto"
          >
            {language === 'en' ? 'العربية' : 'English'}
          </button>
        </div>
        <div className="mt-4 p-2 bg-[#b98e4c] text-[#1a2c46] rounded-md shadow">
          <p className="text-center text-lg font-semibold">{t('step_indicator', { current: currentStepIndex + 1, total: stepsConfig.length })}: {stepsConfig[currentStepIndex].name}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto bg-white p-6 sm:p-8 rounded-lg shadow-xl">
        <CurrentStepComponent 
            formData={formData} 
            onChange={handleDataChange} 
            errors={componentErrors} 
            t={t} 
            openScheduleModal={openScheduleModal}
            setFormData={setFormData} 
        />
        <StepNavigation
          onNext={handleNext}
          onPrevious={handlePrevious}
          currentStep={currentStepIndex}
          totalSteps={stepsConfig.length}
          onPrint={currentStepIndex === stepsConfig.length - 1 ? handlePrint : undefined}
          onExportToExcel={currentStepIndex === stepsConfig.length - 1 ? handleExportToExcel : undefined}
          t={t}
        />
      </main>

      {currentSchedule && (
         <ScheduleModalWrapper 
            title={getScheduleTitle(currentSchedule.name)} 
            onClose={() => closeScheduleModal()} 
            onSave={() => closeScheduleModal(currentSchedule.data)}
            t={t}
        >
          {renderCurrentSchedule()}
        </ScheduleModalWrapper>
      )}

      <footer className="mt-12 text-center text-sm text-[#1a2c46]">
        <p>&copy; {new Date().getFullYear()} {t('gavel_tax_consultancy_name')}. {t('all_rights_reserved')}</p>
        <p>
          {t('powered_by_text')}{' '}
          <a href="https://tikaniyyat.com" target="_blank" rel="noopener noreferrer" className="text-[#b98e4c] hover:underline">
            TIKANIYYAT
          </a>
        </p>
      </footer>
    </div>
  );
};

export default App;